import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { LifeBuoy, Send, X, Loader2, Sparkles } from "lucide-react";

/**
 * GuideChat — floating support assistant powered by OAK Guide
 * (gemini-3-flash via /api/guide/chat).
 *
 * UX:
 *  • FAB anchored bottom-LEFT (so it never collides with the OAK-Pad
 *    FAB on the right).
 *  • Tap to open a glass drawer with a message thread + composer.
 *  • Multi-turn: last 10 messages are sent back to the server every call
 *    so the model has context. Thread is persisted in localStorage so it
 *    survives reloads.
 *  • FREE — never deducts credits.
 */

const STORAGE_KEY = "oak_guide_thread_v1";
const MAX_LOCAL_MESSAGES = 50;

const SEED_GREETING = {
  role: "assistant",
  content:
    "Hi — I'm OAK Guide. Ask me anything about credits, paths, the OAK-Pad, or how to upgrade. I'm not a tutor — for learning content, head to the Academy.",
  ts: Date.now(),
};

function readThread() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [SEED_GREETING];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return [SEED_GREETING];
    return parsed;
  } catch {
    return [SEED_GREETING];
  }
}

function writeThread(messages) {
  try {
    const trimmed = messages.slice(-MAX_LOCAL_MESSAGES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    /* quota / private mode — ignore */
  }
}

export default function GuideChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => readThread());
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => writeThread(messages), [messages]);

  useEffect(() => {
    if (!open) return;
    // Auto-scroll to the bottom whenever the thread grows or the panel opens.
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages, sending]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  // Allow other surfaces (e.g. DailyLimitModal "Ask OAK Guide" link, or
  // the Landing-page "Claim your AI study assistant" CTA persisted across
  // the OAuth round-trip) to open the panel and optionally pre-fill the
  // composer with a contextual question. Detail shape: { prefill?: string }.
  useEffect(() => {
    const onOpen = (e) => {
      setOpen(true);
      const prefill = e?.detail?.prefill;
      if (typeof prefill === "string" && prefill.trim()) {
        setInput(prefill);
      }
    };
    window.addEventListener("oak:open-guide", onOpen);
    return () => window.removeEventListener("oak:open-guide", onOpen);
  }, []);

  // Post-auth handoff: if Landing dropped a `oak_guide_pending_prefill` in
  // localStorage before sending the visitor through Google OAuth, consume
  // it on first mount → auto-open the panel with the question seeded.
  // The localStorage key is wiped only AFTER the timer fires, so React
  // StrictMode's intentional mount→unmount→mount cycle in dev doesn't
  // delete the key on the first pass and starve the second pass.
  useEffect(() => {
    let prefill;
    try { prefill = localStorage.getItem("oak_guide_pending_prefill"); } catch {}
    if (!prefill) return;
    // Small delay so the welcome / dashboard skeleton finishes painting
    // before the chat slides in — feels less jarring.
    const t = setTimeout(() => {
      try { localStorage.removeItem("oak_guide_pending_prefill"); } catch {}
      setOpen(true);
      setInput(prefill);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: "user", content: text, ts: Date.now() }];
    setMessages(next);
    setInput("");
    setSending(true);
    setError(null);
    try {
      // Send only role + content (the server doesn't need ts).
      const history = next.slice(0, -1).map((m) => ({ role: m.role, content: m.content }));
      const { data } = await api.post("/guide/chat", { message: text, history });
      const reply = (data?.reply || "").trim();
      if (!reply) throw new Error("Empty reply");
      setMessages((curr) => [...curr, { role: "assistant", content: reply, ts: Date.now() }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Guide is offline.";
      setError(detail);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearThread = () => {
    setMessages([SEED_GREETING]);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 left-4 md:bottom-8 md:left-8 z-[60] pointer-events-auto inline-flex items-center gap-2 rounded-full px-4 py-2.5 bg-[#0A192F]/95 hover:bg-[#0A192F] text-[#9bffcc] shadow-[0_12px_40px_-12px_rgba(0,255,157,0.35)] border border-[#00FF9D]/40 transition"
        data-testid="guide-fab"
        aria-label="Open OAK Guide support chat"
      >
        <LifeBuoy size={16} />
        <span className="text-[13px] font-display font-semibold">OAK Guide</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-4 left-4 md:bottom-8 md:left-8 z-[70] pointer-events-auto w-[min(92vw,380px)] h-[min(78vh,560px)] flex flex-col rounded-2xl border border-white/10 bg-[#0A192F] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
      data-testid="guide-panel"
      role="dialog"
      aria-label="OAK Guide support chat"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <div className="rounded-full bg-[#00FF9D]/15 p-1.5">
            <Sparkles size={13} className="text-[#00FF9D]" />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-display font-semibold text-white truncate">OAK Guide</div>
            <div className="text-[10px] text-white/45 font-mono-oak">free · powered by Oak AI Campus</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={clearThread}
            className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white/85 px-2 py-1 rounded-md transition"
            data-testid="guide-clear"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full p-1.5 text-white/55 hover:text-white hover:bg-white/10 transition"
            aria-label="Close OAK Guide"
            data-testid="guide-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        data-testid="guide-messages"
      >
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`guide-msg-${m.role}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap break-words ${
                m.role === "user"
                  ? "bg-[#0044FF] text-white"
                  : "bg-white/[0.06] text-white/90 border border-white/5"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="guide-typing">
            <div className="rounded-2xl px-3 py-2 bg-white/[0.06] border border-white/5 inline-flex items-center gap-2 text-white/60 text-[12px]">
              <Loader2 size={12} className="animate-spin" />
              Thinking…
            </div>
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 text-rose-200 text-[12px] px-3 py-2" data-testid="guide-error">
            {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="px-3 py-3 border-t border-white/10 flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about credits, features, or how to upgrade…"
          rows={1}
          className="flex-1 resize-none rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00FF9D]/40 focus:outline-none text-[13px] text-white placeholder:text-white/35 px-3 py-2 max-h-32"
          data-testid="guide-input"
          maxLength={1500}
        />
        <button
          type="button"
          onClick={send}
          disabled={!input.trim() || sending}
          className="rounded-xl bg-[#00FF9D] hover:bg-[#00FF9D]/90 disabled:opacity-40 disabled:cursor-not-allowed text-[#0A192F] p-2 transition"
          data-testid="guide-send"
          aria-label="Send message"
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}
